/**
 * TableMergePanel - Tables Page Style
 *
 * Floor/section hierarchy, clean card styling matching /tables page
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  People as PeopleIcon,
  TableRestaurant as TableIcon,
  CallMerge as MergeIcon,
  CallSplit as SplitIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Apartment as FloorIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useReservations } from '../../context/ReservationContext';
import { useConfirm } from '../../context/ConfirmContext';
import { tableCompatibilityApi } from '../../api/reservationApi';
import AppIconButton from '../ui/AppIconButton';
import { useTheme } from '../../context/ThemeContext';
import type { TableGroup, TableGroupRequest, ReservableTable, WaitlistEntry } from '../../types/reservation.types';
import toast from 'react-hot-toast';

// ============================================================================
// STYLES
// ============================================================================

const styles = `
  .merge-panel {
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    overflow: hidden;
  }

  .merge-panel-header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #E2E8F0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .merge-button {
    background: var(--action);
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.8125rem;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .merge-button:hover {
    background: var(--action-hover);
    transform: translateY(-1px);
  }

  .group-card {
    padding: 1rem;
    border-bottom: 1px solid #F1F5F9;
    transition: background 0.15s ease;
  }

  .group-card:hover {
    background: #F8FAFC;
  }

  .group-card:last-child {
    border-bottom: none;
  }

  .table-grid-item {
    border: 2px solid #E2E8F0;
    border-radius: 10px;
    padding: 1rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: white;
  }

  .table-grid-item:hover:not(.disabled) {
    border-color: var(--action);
    background: #FFFBEB;
  }

  .table-grid-item.selected {
    border-color: var(--action);
    background: #FEF3C7;
  }

  .table-grid-item.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .floor-section {
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    margin-bottom: 0.75rem;
    overflow: hidden;
  }

  .floor-header {
    padding: 0.75rem 1rem;
    background: #F8FAFC;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .floor-header:hover {
    background: #F1F5F9;
  }

  .floor-content {
    padding: 1rem;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(85px, 1fr));
    gap: 0.75rem;
  }

  .selection-summary {
    background: #FEF3C7;
    border: 1px solid #FCD34D;
    border-radius: 10px;
    padding: 1rem;
    margin-top: 1rem;
  }
`;

interface TableMergePanelProps {
  availableTables: ReservableTable[];
  onGroupCreated?: (group: TableGroup) => void;
  onGroupClosed?: (groupId: number) => void;
  className?: string;
  // Waitlist table selection mode
  waitlistEntry?: WaitlistEntry;
  waitlistAction?: 'notify' | 'seat';
  onWaitlistTableSelected?: (tableId: number, tableGroupId?: number) => void;
  onWaitlistCancel?: () => void;
}

const TableMergePanel: React.FC<TableMergePanelProps> = ({
  availableTables,
  onGroupCreated,
  onGroupClosed,
  className = '',
  waitlistEntry,
  waitlistAction,
  onWaitlistTableSelected,
  onWaitlistCancel,
}) => {
  const {
    tableGroups,
    createTableGroup,
    closeTableGroup,
    isTableMergeEnabled,
  } = useReservations();

  const { theme } = useTheme();
  const confirm = useConfirm();
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [selectedTables, setSelectedTables] = useState<number[]>([]);
  const [compatibleTables, setCompatibleTables] = useState<Map<number, number[]>>(new Map());
  const [partySize, setPartySize] = useState<number>(0);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFloors, setExpandedFloors] = useState<Set<string>>(new Set());

  // Load compatibility rules
  useEffect(() => {
    const loadCompatibility = async () => {
      try {
        const rules = await tableCompatibilityApi.getAll();
        const compatMap = new Map<number, number[]>();

        rules.forEach((rule) => {
          if (rule.isActive) {
            if (!compatMap.has(rule.table1Id)) {
              compatMap.set(rule.table1Id, []);
            }
            if (!compatMap.has(rule.table2Id)) {
              compatMap.set(rule.table2Id, []);
            }
            compatMap.get(rule.table1Id)!.push(rule.table2Id);
            compatMap.get(rule.table2Id)!.push(rule.table1Id);
          }
        });

        setCompatibleTables(compatMap);
      } catch (err) {
        console.error('Error loading compatibility rules:', err);
      }
    };

    if (isTableMergeEnabled) {
      loadCompatibility();
    }
  }, [isTableMergeEnabled]);

  // Group tables by floor
  const tablesByFloor = useMemo(() => {
    const grouped = new Map<string, ReservableTable[]>();

    availableTables.forEach((table) => {
      const floorKey = table.floorName || 'Other';
      if (!grouped.has(floorKey)) {
        grouped.set(floorKey, []);
      }
      grouped.get(floorKey)!.push(table);
    });

    // Initialize expanded floors
    if (expandedFloors.size === 0 && grouped.size > 0) {
      const firstFloor = grouped.keys().next().value;
      if (firstFloor) {
        setExpandedFloors(new Set([firstFloor]));
      }
    }

    return grouped;
  }, [availableTables]);

  if (!isTableMergeEnabled) {
    return null;
  }

  // Show both PENDING and ACTIVE groups (RELEASED groups are closed)
  const activeGroups = tableGroups.filter((g) => g.status === 'PENDING' || g.status === 'ACTIVE');

  const canMergeWith = (tableId: number): boolean => {
    // If no tables selected yet, any table can be selected
    if (selectedTables.length === 0) return true;

    // If no compatibility rules defined, allow any tables to be merged
    if (compatibleTables.size === 0) return true;

    // Check if this table is compatible with at least one selected table
    return selectedTables.some((selectedId) => {
      const compatList = compatibleTables.get(selectedId);
      return compatList?.includes(tableId);
    });
  };

  const toggleTable = (tableId: number) => {
    // In waitlist mode, clicking a table assigns it
    if (waitlistEntry && onWaitlistTableSelected) {
      onWaitlistTableSelected(tableId, undefined);
      return;
    }

    if (selectedTables.includes(tableId)) {
      setSelectedTables((prev) => prev.filter((id) => id !== tableId));
    } else if (canMergeWith(tableId)) {
      setSelectedTables((prev) => [...prev, tableId]);
    }
  };

  const handleSelectGroupForWaitlist = (groupId: number) => {
    if (waitlistEntry && onWaitlistTableSelected) {
      onWaitlistTableSelected(0, groupId);
    }
  };

  const toggleFloor = (floorName: string) => {
    setExpandedFloors((prev) => {
      const next = new Set(prev);
      if (next.has(floorName)) {
        next.delete(floorName);
      } else {
        next.add(floorName);
      }
      return next;
    });
  };

  const getTotalCapacity = (): number => {
    return availableTables
      .filter((t) => selectedTables.includes(t.id))
      .reduce((sum, t) => sum + t.seatingCapacity, 0);
  };

  const handleCreateGroup = async () => {
    if (selectedTables.length < 2) {
      setError('Select at least 2 tables to merge');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: TableGroupRequest = {
        tableIds: selectedTables,
        partySize: partySize || undefined,
        primaryTableId: selectedTables[0],
        name: groupName || undefined,
      };

      const group = await createTableGroup(data);
      toast.success('Tables merged successfully');
      onGroupCreated?.(group);
      handleCloseMergeDialog();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to merge tables');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseGroup = async (groupId: number) => {
    if (!(await confirm({
      title: 'Unmerge these tables?',
      description: 'The tables will be split back into individual tables and the group released.',
      confirmText: 'Unmerge',
      cancelText: 'Keep merged',
    }))) return;
    try {
      setLoading(true);
      await closeTableGroup(groupId);
      toast.success('Tables unmerged');
      onGroupClosed?.(groupId);
    } catch (err) {
      toast.error('Failed to unmerge tables');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseMergeDialog = () => {
    setMergeDialogOpen(false);
    setSelectedTables([]);
    setPartySize(0);
    setGroupName('');
    setError(null);
  };

  const getTable = (id: number): ReservableTable | undefined => {
    return availableTables.find((t) => t.id === id);
  };

  return (
    <>
      <style>{styles}</style>
      <div className={`merge-panel ${className}`}>
        {/* Waitlist Mode Banner */}
        {waitlistEntry && (
          <div
            style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
              borderBottom: '1px solid var(--status-warn-border)',
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--status-warn-text)' }}>
                  Select Table for: {waitlistEntry.customerName}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--status-warn-text)' }}>
                  Party of {waitlistEntry.partySize} • {waitlistAction === 'notify' ? 'Notify' : 'Seat'} customer
                </Typography>
              </div>
              {onWaitlistCancel && (
                <Button
                  size="small"
                  onClick={onWaitlistCancel}
                  sx={{ color: 'var(--status-warn-text)' }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="merge-panel-header">
          <div>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              {waitlistEntry ? 'Select a Table' : 'Table Groups'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
              {waitlistEntry
                ? 'Click a table or merged group to assign'
                : `${activeGroups.length} active merge${activeGroups.length !== 1 ? 's' : ''}`}
            </Typography>
          </div>
          {!waitlistEntry && (
            <button className="merge-button" onClick={() => setMergeDialogOpen(true)}>
              <MergeIcon sx={{ fontSize: 18 }} />
              Merge Tables
            </button>
          )}
        </div>

        {/* Active Groups List */}
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {activeGroups.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <TableIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} /> {/* basil-todo */}
              <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
                No active table merges
              </Typography>
            </div>
          ) : (
            activeGroups.map((group) => (
              <div
                key={group.id}
                className="group-card"
                style={{
                  cursor: waitlistEntry ? 'pointer' : 'default',
                  border: waitlistEntry ? '2px solid transparent' : undefined,
                  transition: 'border-color 0.2s ease',
                }}
                onClick={() => waitlistEntry && handleSelectGroupForWaitlist(group.id)}
                onMouseEnter={(e) => waitlistEntry && (e.currentTarget.style.borderColor = 'var(--action)')}
                onMouseLeave={(e) => waitlistEntry && (e.currentTarget.style.borderColor = 'transparent')}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <LinkIcon sx={{ color: 'var(--status-warn)' }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      {group.name || `Group #${group.groupCode}`}
                    </Typography>
                    {waitlistEntry && (
                      <Chip
                        size="small"
                        label="Click to assign"
                        sx={{
                          backgroundColor: 'var(--status-warn-bg)',
                          color: 'var(--status-warn-text)',
                          fontWeight: 500,
                          fontSize: '0.65rem',
                        }}
                      />
                    )}
                  </div>
                  {!waitlistEntry && (
                    <AppIconButton
                      icon={<SplitIcon fontSize="small" />}
                      tooltip="Unmerge tables"
                      variant="danger"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseGroup(group.id);
                      }}
                      data-testid={`unmerge-group-${group.id}`}
                    />
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {group.tableNames.map((name, idx) => (
                    <Chip
                      key={idx}
                      size="small"
                      icon={<TableIcon sx={{ fontSize: 14 }} />}
                      label={name}
                      sx={{
                        backgroundColor: 'var(--bg-sunken)',
                        color: 'var(--text-secondary)',
                        fontWeight: 500,
                        '& .MuiChip-icon': { color: 'var(--text-tertiary)' },
                      }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                  <span className="flex items-center gap-1">
                    <PeopleIcon sx={{ fontSize: 16 }} />
                    Capacity: {group.totalCapacity}
                  </span>
                  {group.partySize && <span>Party: {group.partySize}</span>}
                  {group.orderLineId && (
                    <Chip
                      size="small"
                      label="Has Order"
                      sx={{
                        backgroundColor: 'var(--status-ok-bg)',
                        color: 'var(--status-ok)',
                        fontWeight: 500,
                        fontSize: '0.6875rem',
                      }}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Merge Dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={handleCloseMergeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid var(--border-subtle)', pb: 2 }}>
          <div className="flex items-center gap-2">
            <MergeIcon sx={{ color: 'var(--status-warn)' }} />
            <span style={{ fontWeight: 600 }}>Merge Tables</span>
          </div>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, overflowY: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" sx={{ color: 'var(--text-tertiary)', mb: 3 }}>
            Select tables from the same or adjacent areas to merge them.
          </Typography>

          {/* Party Size */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'var(--text-primary)' }}>
              Party Size (optional)
            </Typography>
            <div className="flex items-center gap-2">
              <AppIconButton
                icon={<span style={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1 }}>-</span>}
                tooltip="Decrease party size"
                variant="secondary"
                size="small"
                onClick={() => setPartySize((p) => Math.max(0, p - 1))}
                data-testid="party-size-decrease"
              />
              <Typography
                variant="h6"
                sx={{
                  width: 48,
                  textAlign: 'center',
                  fontWeight: 600,
                  color: partySize > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)',
                }}
              >
                {partySize || '-'}
              </Typography>
              <AppIconButton
                icon={<span style={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1 }}>+</span>}
                tooltip="Increase party size"
                variant="secondary"
                size="small"
                onClick={() => setPartySize((p) => p + 1)}
                data-testid="party-size-increase"
              />
            </div>
          </Box>

          {/* Tables by Floor */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'var(--text-primary)' }}>
            Select Tables
          </Typography>

          {Array.from(tablesByFloor.entries()).map(([floorName, tables]) => {
            const isExpanded = expandedFloors.has(floorName);
            const selectedInFloor = tables.filter((t) => selectedTables.includes(t.id)).length;

            return (
              <div key={floorName} className="floor-section">
                <div className="floor-header" onClick={() => toggleFloor(floorName)}>
                  <div className="flex items-center gap-2">
                    <FloorIcon sx={{ color: 'var(--text-tertiary)', fontSize: 20 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      {floorName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
                      {tables.length} tables
                    </Typography>
                    {selectedInFloor > 0 && (
                      <Chip
                        size="small"
                        label={`${selectedInFloor} selected`}
                        sx={{
                          backgroundColor: 'var(--status-warn-bg)',
                          color: 'var(--status-warn-text)',
                          fontWeight: 600,
                          fontSize: '0.6875rem',
                        }}
                      />
                    )}
                  </div>
                  {isExpanded ? (
                    <CollapseIcon sx={{ color: 'var(--text-tertiary)' }} />
                  ) : (
                    <ExpandIcon sx={{ color: 'var(--text-tertiary)' }} />
                  )}
                </div>
                <Collapse in={isExpanded}>
                  <div className="floor-content">
                    {tables.map((table) => {
                      const isSelected = selectedTables.includes(table.id);
                      const canMerge = canMergeWith(table.id);

                      return (
                        <div
                          key={table.id}
                          onClick={() => (canMerge || isSelected) && toggleTable(table.id)}
                          className={`table-grid-item ${isSelected ? 'selected' : ''} ${
                            !canMerge && !isSelected ? 'disabled' : ''
                          }`}
                        >
                          {isSelected && (
                            <CheckIcon
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                fontSize: 16,
                                color: 'var(--status-warn)',
                              }}
                            />
                          )}
                          <TableIcon
                            sx={{
                              fontSize: 28,
                              color: isSelected ? 'var(--action)' : 'var(--text-tertiary)',
                              mb: 0.5,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isSelected ? '#92400E' : '#475569',
                            }}
                          >
                            {table.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'var(--text-tertiary)' }}>
                            {table.seatingCapacity} seats
                          </Typography>
                        </div>
                      );
                    })}
                  </div>
                </Collapse>
              </div>
            );
          })}

          {/* Selection Summary */}
          {selectedTables.length > 0 && (
            <div className="selection-summary">
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'var(--status-warn-text)' }}>
                Selected Tables
              </Typography>
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedTables.map((id) => {
                  const table = getTable(id);
                  return (
                    <Chip
                      key={id}
                      label={table?.name || `Table ${id}`}
                      onDelete={() => toggleTable(id)}
                      sx={{
                        backgroundColor: '#FCD34D', /* basil-todo */
                        color: 'var(--status-warn-text)',
                        fontWeight: 600,
                        '& .MuiChip-deleteIcon': { color: 'var(--status-warn-text)' },
                      }}
                    />
                  );
                })}
              </div>
              <Typography variant="body2" sx={{ color: 'var(--status-warn-text)' }}>
                Combined capacity: <strong>{getTotalCapacity()} seats</strong>
              </Typography>
            </div>
          )}

          {/* Group Name */}
          <TextField
            label="Group Name (optional)"
            fullWidth
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="e.g., VIP Section, Birthday Party"
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid var(--border-subtle)' }}>
          <Button onClick={handleCloseMergeDialog} sx={{ color: 'var(--text-tertiary)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={loading || selectedTables.length < 2}
            sx={{
              background: 'var(--action)',
              '&:hover': { background: 'var(--action-hover)' },
              '&:disabled': { background: 'var(--bg-sunken)', color: 'var(--text-tertiary)' },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Merge Tables'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TableMergePanel;
